import useCombinedRefs from "@hooks/useCombinedRefs";
import { TransitionEvents } from "@lib/types/generic";
import { classNames } from "@lib/utils/generic";
import React, {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  PropsWithChildren,
  useRef,
} from "react";
import { CSSTransition } from "react-transition-group";

interface Props extends TransitionEvents {
  in: boolean;
  appear?: boolean;
  properties?: string;
  timeout?: number;
  easing?: string;
  direction?: "down" | "left" | "right" | "up";
  distance?: "tiny" | "short" | "normal" | "long";
  unmountOnExit?: boolean;
  mountOnEnter?: boolean;
}

export type SlideProps = Props;

const Slide = forwardRef<HTMLElement, PropsWithChildren<Props>>((props, forwardedRef) => {
  const {
    in: _in,
    appear,
    properties = "transform, opacity",
    timeout = 300,
    easing = "ease",

    direction = "down",
    distance = "normal",

    unmountOnExit = true,
    mountOnEnter = true,

    children,

    ...transitionEvents
  } = props;

  const nodeRef = useRef<HTMLElement>(null);
  const ref = useCombinedRefs(nodeRef, forwardedRef);

  return (
    <CSSTransition
      classNames="slide"
      in={_in}
      appear={appear}
      timeout={timeout}
      unmountOnExit={unmountOnExit}
      mountOnEnter={mountOnEnter}
      nodeRef={ref}
      {...transitionEvents}>
      {isValidElement(children) &&
        cloneElement(Children.only(children) as any, {
          style: {
            transitionDuration: `${timeout}ms`,
            transitionTimingFunction: easing,
            transitionProperty: properties,
            ...children.props.style,
          },
          className: classNames("slide-transition", direction, distance, children.props.className),
          ref,
        })}
    </CSSTransition>
  );
});

export default Slide;
